import React, { useEffect, useRef, useState } from 'react';
import get from 'lodash/get';
import {
  FileInput,
  FileInputClasses,
  HttpError,
  useRecordContext,
} from 'react-admin';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import EditFieldLayout from '../components/layout/EditFieldLayout';
import errorLogger from '../utilities/errorLogger';

const defaultAccept = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/webp': ['.webp'],
};

const defaultMaxSize = 1024 * 1024;

export type ImagePreview =
  | { status: 'loading' }
  | { status: 'missing' }
  | { status: 'failed' }
  | { status: 'loaded'; url: string };

export const formatFileSize = (bytes: number) => {
  if (bytes >= 1024 * 1024) return `${Number((bytes / (1024 * 1024)).toFixed(1))} MB`;
  return `${Math.round(bytes / 1024)} KB`;
};

// The API enforces the limit too, this only spares an upload that is bound to be rejected
export const validateImageSize = (maxSize: number) => (value: unknown) => {
  const size = (value as { rawFile?: File } | null)?.rawFile?.size;
  if (size === undefined || size <= maxSize) return undefined;
  return `The image must be ${formatFileSize(maxSize)} or smaller`;
};

export const formatImageValue = (value: unknown) => (
  typeof value === 'string' ? { storedKey: value } : value
);

export const loadImagePreview = async (
  imageKey: string | null | undefined,
  getPreviewUrl: () => Promise<string>,
): Promise<ImagePreview> => {
  if (!imageKey) return { status: 'missing' };

  try {
    return { status: 'loaded', url: await getPreviewUrl() };
  } catch (error) {
    if (error instanceof HttpError && error.status === 404) return { status: 'missing' };
    errorLogger(error);
    return { status: 'failed' };
  }
};

export const ImageThumbnail = ({
  preview,
  emptyText,
}: {
  preview: ImagePreview;
  emptyText: string;
}) => {
  if (preview.status === 'loading') return <CircularProgress size={20} />;
  if (preview.status === 'missing') return <Typography variant="body2">{emptyText}</Typography>;
  if (preview.status === 'failed') return <Typography variant="body2">Could not load the image</Typography>;

  return (
    <Box
      component="img"
      src={preview.url}
      alt=""
      sx={{ maxHeight: 80, maxWidth: 200, objectFit: 'contain' }}
    />
  );
};

export const ImagePreviewField = ({
  imageKey,
  emptyText,
  getPreviewUrl,
}: {
  imageKey: string | null | undefined;
  emptyText: string;
  getPreviewUrl: () => Promise<string>;
}) => {
  const [preview, setPreview] = useState<ImagePreview>({ status: 'loading' });

  // Kept out of the effect dependencies so that a caller passing a new function on every render
  // does not request the preview again on every render.
  const getPreviewUrlRef = useRef(getPreviewUrl);
  getPreviewUrlRef.current = getPreviewUrl;

  // The preview URL relies on signed cookies that expire quickly, so it is requested again
  // whenever the field is shown, which includes after every save, and when the image changes.
  useEffect(() => {
    let cancelled = false;
    setPreview({ status: 'loading' });
    loadImagePreview(imageKey, getPreviewUrlRef.current).then((loadedPreview) => {
      if (!cancelled) setPreview(loadedPreview);
    });
    return () => {
      cancelled = true;
    };
  }, [imageKey]);

  return <ImageThumbnail preview={preview} emptyText={emptyText} />;
};

export const ImageInputPreview = ({
  emptyText,
  getPreviewUrl,
}: {
  emptyText: string;
  getPreviewUrl: () => Promise<string>;
}) => {
  const file = useRecordContext();

  if (file?.rawFile) {
    return <ImageThumbnail preview={{ status: 'loaded', url: file.src }} emptyText={emptyText} />;
  }

  return (
    <ImagePreviewField
      imageKey={file?.storedKey}
      emptyText={emptyText}
      getPreviewUrl={getPreviewUrl}
    />
  );
};

const EditImageField = ({
  source,
  label,
  showEdit,
  getPreviewUrl,
  emptyText = 'No image',
  accept = defaultAccept,
  maxSize = defaultMaxSize,
}: {
  source: string;
  label: string | false;
  showEdit: boolean;
  getPreviewUrl: () => Promise<string>;
  emptyText?: string;
  accept?: { [mime: string]: string[] };
  maxSize?: number;
}) => {
  const imageKey: string | null | undefined = get(useRecordContext(), source);

  return (
    <EditFieldLayout
      source={source}
      label={label}
      showEdit={showEdit}
      inputElement={(
        <FileInput
          source={source}
          label={false}
          accept={accept}
          format={formatImageValue}
          validate={validateImageSize(maxSize)}
          helperText={`${Object.values(accept).flat().join(', ')} up to ${formatFileSize(maxSize)}`}
          sx={{
            [`& .${FileInputClasses.removeButton}`]: { display: 'flex', alignItems: 'center', gap: 1 },
          }}
        >
          <ImageInputPreview emptyText={emptyText} getPreviewUrl={getPreviewUrl} />
        </FileInput>
      )}
      fieldElement={(
        <ImagePreviewField
          imageKey={imageKey}
          emptyText={emptyText}
          getPreviewUrl={getPreviewUrl}
        />
      )}
    />
  );
};

export default EditImageField;
